// 書き出しタブ: .fmapply を生成してダウンロード。

import { describeError } from "../core/errors.ts";
import { fmApplyFileName, serializeFmApply } from "../core/fmapply.ts";
import { GESTURE_LABELS_JA, isDefaultTracking, optionsOf } from "../core/types.ts";
import { store } from "../store/state.ts";
import { downloadBytes, el } from "./dom.ts";
import { getPendingSendTarget, openSendDialog, relaySettings } from "./transferView.ts";

function summary(): HTMLElement {
  const project = store.project;
  const list = el("ul", { class: "warn-list" });
  let assigned = 0;
  if (project) {
    for (const hand of ["left", "right"] as const) {
      project.gestures[hand].forEach((clipIndex, g) => {
        if (clipIndex === null) return;
        assigned++;
        const clip = store.clipByIndex(clipIndex);
        list.appendChild(
          el("li", { text: `${hand === "left" ? "左手" : "右手"} ${GESTURE_LABELS_JA[g]} → ${clip?.name ?? "?"}` }),
        );
      });
    }
    for (const c of project.combos) {
      if (c.clipIndex === null) continue;
      const clip = store.clipByIndex(c.clipIndex);
      list.appendChild(
        el("li", { text: `左 ${GESTURE_LABELS_JA[c.left]} × 右 ${GESTURE_LABELS_JA[c.right]} → ${clip?.name ?? "?"}（コンビ優先）` }),
      );
    }
  }
  const comboCount = project?.combos.filter((c) => c.clipIndex !== null).length ?? 0;
  const trackingChanged = project?.clips.filter((c) => c.tracking && !isDefaultTracking(c.tracking)).length ?? 0;
  const dl = el("dl", { class: "stat-grid" });
  const add = (k: string, v: string): void => {
    dl.appendChild(el("dt", { text: k }));
    dl.appendChild(el("dd", { text: v }));
  };
  add("クリップ数", `${project?.clips.length ?? 0} 個`);
  add("割り当て数", `${assigned} 個`);
  add("コンビネーション", `${comboCount} 件`);
  add("トラッキング変更", trackingChanged > 0 ? `${trackingChanged} クリップ` : "既定のまま");
  const menuCount = project?.menu.length ?? 0;
  const kfCount = project?.clips.filter((c) => c.type === "keyframe").length ?? 0;
  add("メニュー表情", menuCount > 0 ? `${menuCount} 件` : "なし");
  add("キーフレーム", kfCount > 0 ? `${kfCount} クリップ` : "なし");
  const o = optionsOf(project);
  add(
    "優先する手",
    o.priorityHand === "none"
      ? "なし（後勝ち）"
      : `${o.priorityHand === "left" ? "左手" : "右手"}${o.suppressOtherHandOnPriority ? "（反対の手を抑制）" : ""}`,
  );
  add("適用モード", project?.applyMode === "ndmf" ? "ndmf（非破壊）" : "direct（FX 直接編集）");

  return el(
    "div",
    { class: "card" },
    dl,
    assigned > 0 || comboCount > 0
      ? list
      : el("p", { class: "muted", text: "ジェスチャーへの割り当てがまだありません。" }),
  );
}

function targetAvatarNotice(): HTMLElement {
  const name = store.avatar?.manifest.avatarName ?? "-";
  const current = store.savedAvatars.find((s) => s.id === store.avatar?.id);
  const savedAt = current ? new Date(current.savedAt).toLocaleString("ja-JP") : null;
  const updatedAt = store.project?.updatedAt ? new Date(store.project.updatedAt).toLocaleString("ja-JP") : null;
  // 同名で複数バージョンが保存されている場合、名前だけでは区別できないため保存日時も出す
  const sameNameCount = store.savedAvatars.filter((s) => s.name === name).length;
  const otherCount = store.savedAvatars.length - 1;
  return el(
    "div",
    { class: "banner info", style: { marginBottom: "12px" } },
    el(
      "div",
      { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" } },
      el(
        "div",
        {},
        el("span", { class: "muted", text: "書き出し対象のアバター: " }),
        el("strong", { text: name }),
        savedAt ? el("span", { class: "muted", text: `（保存日時: ${savedAt}）` }) : null,
      ),
      el("button", { class: "btn", text: "切り替える", onclick: () => store.setTab("avatar") }),
    ),
    updatedAt ? el("p", { class: "muted", text: `作業の最終更新日時: ${updatedAt}` }) : null,
    sameNameCount > 1
      ? el("p", {
          class: "muted",
          text: `同名の「${name}」が ${sameNameCount} 件保存されています。上記の保存日時・最終更新日時のものが書き出されます。`,
        })
      : otherCount > 0
        ? el("p", { class: "muted", text: `他に ${otherCount} 件のアバターが保存されています。書き出されるのは上記のアバターのみです。` })
        : null,
  );
}

function doExport(): void {
  const avatar = store.avatar;
  const project = store.project;
  if (!avatar || !project) return;
  try {
    const bytes = serializeFmApply({
      avatarManifest: { avatarName: avatar.manifest.avatarName, avatarUid: avatar.manifest.avatarUid },
      project,
    });
    downloadBytes(bytes, fmApplyFileName(avatar.manifest.avatarName));
    store.setBanner({
      kind: "info",
      title: ".fmapply を書き出しました",
      detail: "Unity の FaceMaker ウィンドウで読み込んでアバターに適用してください。",
      hint: null,
    });
  } catch (e) {
    const d = describeError(e);
    store.setBanner({ kind: "error", title: d.title, detail: d.detail, hint: d.hint });
  }
}

/** 優先する手（SPEC_FMAPPLY §4.3 priorityHand / suppressOtherHandOnPriority） */
function priorityFields(): HTMLElement {
  const o = optionsOf(store.project);

  const sel = el(
    "select",
    {
      class: "select",
      onchange: (e: Event) =>
        store.setApplyOption("priorityHand", (e.target as HTMLSelectElement).value as "left" | "right" | "none"),
    },
    el("option", { value: "none", text: "なし — 両手が同時に出たら右手が上（従来どおり）" }),
    el("option", { value: "left", text: "左手を優先" }),
    el("option", { value: "right", text: "右手を優先" }),
  ) as HTMLSelectElement;
  sel.value = o.priorityHand;

  const box = el("input", { type: "checkbox", id: "opt-suppress" }) as HTMLInputElement;
  box.checked = o.suppressOtherHandOnPriority;
  box.disabled = o.priorityHand === "none";
  box.addEventListener("change", () => store.setApplyOption("suppressOtherHandOnPriority", box.checked));

  return el(
    "div",
    {},
    el("div", { class: "field" }, el("label", { text: "優先する手" }), sel),
    el(
      "label",
      { class: "radio-row", for: "opt-suppress" },
      box,
      el(
        "span",
        {},
        el("span", { class: "r-title", text: "反対の手の表情を抑制する" }),
        el("span", { class: "r-hint", text: "優先する手がニュートラル以外のとき、もう一方の手の表情を出さない" }),
      ),
    ),
    el("p", {
      class: "muted",
      text: "左右コンビネーションを設定した組み合わせは、優先設定より常に優先されます。",
    }),
  );
}

/** 全クリップを .anim としても書き出すか（ジェスチャー未割り当てのクリップの取り出し用）。 */
function clipExportFields(): HTMLElement {
  const o = optionsOf(store.project);

  const box = el("input", { type: "checkbox", id: "opt-export-all-clips" }) as HTMLInputElement;
  box.checked = o.exportAllClipsAsAnim;
  box.addEventListener("change", () => store.setApplyOption("exportAllClipsAsAnim", box.checked));

  return el(
    "label",
    { class: "radio-row", for: "opt-export-all-clips" },
    box,
    el(
      "span",
      {},
      el("span", { class: "r-title", text: "全クリップを .anim としても書き出す" }),
      el("span", {
        class: "r-hint",
        text: "ジェスチャーやメニューに割り当てていないクリップも含め、全クリップを Assets/FaceMaker/Generated/<アバター名>/Clips に .anim ファイルとして書き出します（NDMF非破壊適用でも、Playモード/アップロードを待たずに参照できます）。",
      }),
    ),
  );
}

/** メニュー設置先（SPEC_FMAPPLY §4.3 menuLabel / menuInstallPath） */
function menuFields(): HTMLElement {
  const o = optionsOf(store.project);

  const labelInput = el("input", { class: "text-input", type: "text", value: o.menuLabel, maxlength: "64" }) as HTMLInputElement;
  labelInput.addEventListener("change", () => {
    const v = labelInput.value.trim();
    if (v.length === 0) {
      labelInput.value = o.menuLabel;
      return;
    }
    store.setApplyOption("menuLabel", v);
  });

  const pathInput = el("input", {
    class: "text-input",
    type: "text",
    value: o.menuInstallPath ?? "",
    placeholder: "（空欄でメニューのルート直下）",
  }) as HTMLInputElement;
  pathInput.addEventListener("change", () => {
    const v = pathInput.value.trim();
    store.setApplyOption("menuInstallPath", v.length === 0 ? null : v);
  });

  return el(
    "div",
    {},
    el("div", { class: "field" }, el("label", { text: "メニューの名前" }), labelInput),
    el("div", { class: "field" }, el("label", { text: "メニューの設置先（/ 区切り）" }), pathInput),
  );
}

/** 「詳細設定」の開閉状態（再描画をまたいで保持する）。 */
let advancedOpen = false;

export function renderExportView(): HTMLElement {
  const root = el("div", {});
  if (!store.avatar || !store.project) {
    root.appendChild(
      el("div", { class: "section" }, el("p", { class: "muted", text: "先に「アバター」タブで .fmavatar を読み込んでください。" })),
    );
    return root;
  }

  const modeSelect = el(
    "select",
    {
      class: "select",
      onchange: (e: Event) => store.setApplyMode((e.target as HTMLSelectElement).value as "ndmf" | "direct"),
    },
    el("option", { value: "ndmf", text: "ndmf — NDMF/Modular Avatar で非破壊適用（推奨）" }),
    el("option", { value: "direct", text: "direct — FX を複製して直接編集" }),
  );
  modeSelect.value = store.project.applyMode;

  // 詳細設定の <details>。設定変更のたびに画面全体が再描画されるので、開閉状態を引き継ぐ
  const advanced = el(
    "details",
    { class: "advanced" },
    el("summary", { text: "詳細設定" }),
    el("div", { class: "field" }, el("label", { text: "適用モード" }), modeSelect),
    priorityFields(),
    menuFields(),
    clipExportFields(),
    relaySettings(),
    el("p", {
      class: "muted",
      text: "ndmf は NDMF/Modular Avatar 経由で非破壊に適用するモードで、標準の既定です。NDMF/Modular Avatar が入っていないプロジェクトでは direct（FX を複製して直接編集）を選んでください。",
    }),
  ) as HTMLDetailsElement;
  advanced.open = advancedOpen;
  advanced.addEventListener("toggle", () => {
    advancedOpen = advanced.open;
  });

  root.appendChild(
    el(
      "div",
      { class: "section" },
      el("h2", { text: "書き出し内容" }),
      targetAvatarNotice(),
      summary(),
      el("button", {
        class: "btn primary block",
        text: getPendingSendTarget() ? "Unity へ直接送信（受信待ち中）" : "リレーで送信（推奨）",
        onclick: () => openSendDialog(optionsOf(store.project)),
      }),
      el("p", {
        class: "muted",
        text: getPendingSendTarget()
          ? "Unity が受信待ちの状態です。ボタンを押すとそのまま Unity へ届きます。"
          : "リレー経由で Unity に送ります。ファイルは暗号化され、鍵はコード文字列としてのみ渡されます（1時間・1回限り）。多くの場合こちらが手軽です。",
      }),
      el("button", { class: "btn block", text: ".fmapply をダウンロード", onclick: doExport }),
      el("p", {
        class: "muted",
        text: "ファイルとして保存し、あとでPCのUnityへ手動で転送したい場合はこちら。",
      }),
      advanced,
    ),
  );

  return root;
}
